import { useWidgetConfig } from "@/context/WidgetConfigContext";
import { Mic, MicOff, PhoneOff, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

interface VoiceActiveScreenProps {
  isMuted: boolean;
  volumeLevel: number;
  onToggleMute: () => void;
  onEndCall: () => void;
}

/**
 * Active call screen with controls and audio visualization
 */
export function VoiceActiveScreen({
  isMuted,
  volumeLevel,
  onToggleMute,
  onEndCall,
}: VoiceActiveScreenProps) {
  const { config } = useWidgetConfig();
  const [callDuration, setCallDuration] = useState(0);

  // Call duration timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format duration as mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col items-center justify-center gap-8 p-8">
      {/* Audio visualization */}
      <div className="relative flex flex-col items-center gap-4">
        {/* Animated rings based on volume */}
        <div className="relative w-32 h-32">
          {/* Outer ring - animated with volume */}
          <div
            className="absolute inset-0 rounded-full transition-all duration-150"
            style={{
              backgroundColor: config.primary_color,
              opacity: 0.1 + volumeLevel * 0.3,
              transform: `scale(${1 + volumeLevel * 0.3})`,
            }}
          />
          {/* Middle ring */}
          <div
            className="absolute inset-3 rounded-full transition-all duration-150"
            style={{
              backgroundColor: config.primary_color,
              opacity: 0.2 + volumeLevel * 0.3,
              transform: `scale(${1 + volumeLevel * 0.2})`,
            }}
          />
          {/* Center circle with icon */}
          <div
            className="absolute inset-6 rounded-full flex items-center justify-center transition-all duration-150"
            style={{
              backgroundColor: config.primary_color,
              transform: `scale(${1 + volumeLevel * 0.1})`,
            }}
          >
            <Volume2
              className="w-10 h-10 transition-all duration-150"
              style={{
                color: config.header_text_color,
                transform: `scale(${1 + volumeLevel * 0.2})`,
              }}
            />
          </div>
        </div>

        {/* Call duration */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-lg font-mono text-foreground">
            {formatDuration(callDuration)}
          </span>
        </div>

        {/* Status text */}
        <p className="text-sm text-muted-foreground">
          {isMuted ? "Microphone muted" : "Speak now..."}
        </p>
      </div>

      {/* Control buttons */}
      <div className="flex items-center gap-4">
        {/* Mute button */}
        <Button
          onClick={onToggleMute}
          size="lg"
          variant="outline"
          className={`w-14 h-14 rounded-full transition-all duration-200 ${
            isMuted
              ? "bg-destructive/20 border-destructive/50 text-destructive hover:bg-destructive/30"
              : "hover:bg-accent"
          }`}
        >
          {isMuted ? (
            <MicOff className="w-6 h-6" />
          ) : (
            <Mic className="w-6 h-6" />
          )}
        </Button>

        {/* End call button */}
        <Button
          onClick={onEndCall}
          size="lg"
          className="w-16 h-16 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <PhoneOff className="w-7 h-7" />
        </Button>
      </div>
    </div>
  );
}
